import { TurboMount } from "turbo-mount";
import { registerComponent } from "turbo-mount/react";
import React from "react";
import { subjectStore } from "./utils/subject_store";
import { toast } from "./utils/toast";

// Import React components
import SyncStatusBadges from "./components/SyncStatusBadges.jsx";
import ProjectSyncStatus from "./components/ProjectSyncStatus.jsx";
import UnsyncedSubjects from "./components/UnsyncedSubjects.jsx";

// Create a TurboMount instance (this also registers the Stimulus controller)
const turboMount = new TurboMount();

// Use the single exported subject store instance
const getSubjectStore = () => {
  return subjectStore;
};

// Create wrapper components that inject the subject store and callback functions
const SyncStatusBadgesWithStore = (props) => {
  return React.createElement(SyncStatusBadges, {
    ...props,
    subjectStore: getSubjectStore()
  });
};

const ProjectSyncStatusWithStore = (props) => {
  const syncAll = async (projectId) => {
    if (!projectId) {
      console.warn('No project ID provided for sync operation');
      return;
    }

    try {
      await getSubjectStore().syncAllPending(projectId);
    } catch (error) {
      console.error('Error syncing all subjects:', error);

      toast.error('Failed to sync subjects. Check your network connection and try again.');
      throw error;
    }
  };

  return React.createElement(ProjectSyncStatus, {
    ...props,
    subjectStore: getSubjectStore(),
    onSyncAll: syncAll
  });
};

const UnsyncedSubjectsWithStore = (props) => {
  const syncSubject = async (subjectId) => {
    if (!subjectId) return;

    try {
      await getSubjectStore().syncToServer(subjectId);
    } catch (error) {
      console.error('Error syncing subject:', error);
      toast.error('Failed to sync subject. Please try again.');
      throw error;
    }
  };

  const syncAll = async () => {
    try {
      const projectId = props.projectId;
      const unsyncedSubjects = await getSubjectStore().getAll(projectId);

      for (const subject of unsyncedSubjects) {
        await getSubjectStore().syncToServer(subject.id);
      }
    } catch (error) {
      console.error('Error syncing all subjects:', error);
      toast.error('Failed to sync all subjects. Please check your network connection and try again.');
      throw error;
    }
  };

  const deleteSubject = async (subjectId) => {
    if (!subjectId) return;

    try {
      await getSubjectStore().delete(subjectId);
    } catch (error) {
      console.error('Error deleting subject:', error);
      toast.error('Failed to delete subject. Please try again.');
      throw error;
    }
  };

  const clearAll = async () => {
    try {
      const projectId = props.projectId;
      const unsyncedSubjects = await getSubjectStore().getAll(projectId);

      for (const subject of unsyncedSubjects) {
        await getSubjectStore().delete(subject.id);
      }
    } catch (error) {
      console.error('Error clearing all subjects:', error);
      toast.error('Failed to clear all subjects. Some may have been deleted successfully.');
      throw error;
    }
  };

  return React.createElement(UnsyncedSubjects, {
    ...props,
    subjectStore: getSubjectStore(),
    onSyncSubject: syncSubject,
    onDeleteSubject: deleteSubject,
    onSyncAll: syncAll,
    onClearAll: clearAll
  });
};

// Register React components with turbo-mount
registerComponent(turboMount, "SyncStatusBadges", SyncStatusBadgesWithStore);
registerComponent(turboMount, "ProjectSyncStatus", ProjectSyncStatusWithStore);
registerComponent(turboMount, "UnsyncedSubjects", UnsyncedSubjectsWithStore);

export { turboMount };